import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FieldLabel } from '../components/FieldLabel';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { useBackRoute } from '../hooks/useBackRoute';
import { useAuth } from '../contexts/AuthContext';
import { fetchTpTechnicianById } from '../services/tpTechniciansService';
import { fetchAppointmentsByTechService } from '../services/serviceRecordsService';
import { categorizeAppointments, getTechnicianDisplayName } from '../utils/appointmentCategories';
import TpAppointmentListTable from '../components/TpAppointmentListTable';
import DateRangeFilterModal from '../components/DateRangeFilterModal';
import OrderCreationPermissionBadge from '../components/OrderCreationPermissionBadge';

const TABS = [
  { id: 'future', label: 'Gelecek Randevular' },
  { id: 'open', label: 'Açık Randevular' },
  { id: 'completed', label: 'Tamamlanan Randevular' },
];

const extractTechServiceId = (u) => {
  if (!u) return null;
  const ts = u.techService ?? u.techServiceId;
  if (!ts) return null;
  if (typeof ts === 'string' && ts.trim()) return ts.trim();
  if (ts && typeof ts === 'object') {
    const id = ts._id ?? ts.id;
    if (id) return typeof id === 'string' ? id : String(id);
  }
  return null;
};

const TechServiceTechnicianDetailPage = () => {
  const { teknisyenId } = useParams();
  const location = useLocation();
  const goBack = useBackRoute('/tech-service/teknisyenler');
  const { user } = useAuth();

  const [technician, setTechnician] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('future');
  const [dateRange, setDateRange] = useState({});
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);

  const techServiceId = useMemo(() => extractTechServiceId(user), [user]);

  const loadData = useCallback(async () => {
    if (!techServiceId || !teknisyenId) return;
    setLoading(true);
    setError('');
    try {
      const [techRes, aptRes] = await Promise.all([
        fetchTpTechnicianById(techServiceId, teknisyenId),
        fetchAppointmentsByTechService(techServiceId, {
          teknisyenId,
          aktif: 'all',
          limit: 100,
          page: 1,
          ...dateRange,
        }),
      ]);
      setTechnician(techRes?.veri?.teknisyen || techRes?.veri || null);
      const list = aptRes?.veri?.randevular || aptRes?.veri || [];
      setAppointments(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || e?.data?.mesaj || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [techServiceId, teknisyenId, dateRange]);

  useEffect(() => {
    if (user?.rol === 'tech-service') loadData();
  }, [user?.rol, loadData]);

  const categorized = useMemo(() => categorizeAppointments(appointments), [appointments]);

  const tabAppointments = useMemo(() => {
    if (activeTab === 'future') return categorized.future;
    if (activeTab === 'open') return categorized.open;
    return categorized.completed;
  }, [activeTab, categorized]);

  const displayName = useMemo(() => {
    if (technician) return getTechnicianDisplayName(technician);
    return location.state?.technicianName || 'Teknisyen';
  }, [technician, location.state?.technicianName]);

  if (user?.rol === 'tech-person') return <Navigate to="/teknisyen" replace />;
  if (user?.rol !== 'tech-service') return <Navigate to="/" replace />;

  const dateFilterLabel = dateRange.baslangicTarihi || dateRange.bitisTarihi
    ? `${dateRange.baslangicTarihi || '…'} – ${dateRange.bitisTarihi || '…'}`
    : null;

  return (
    <main className="main-content tech-service-technician-detail-page">
      <div className="content-header">
        <div>
          <button type="button" className="secondary-btn" onClick={goBack} style={{ marginBottom: '16px' }}>
            <i className="fas fa-arrow-left" />
            <span>Geri Dön</span>
          </button>
          <h1>{displayName}</h1>
        </div>
      </div>

      {loading ? (
        <div className="loading-state"><div className="loading-spinner" /><div className="loading-text">Yükleniyor</div></div>
      ) : error ? (
        <div className="error-state">{error}</div>
      ) : (
        <>
          <div className="service-record-info" style={{ marginBottom: '20px' }}>
            <div className="info-row">
              <div className="info-item">
                <FieldLabel className="info-label" as="span">Kullanıcı Adı</FieldLabel>
                <span className="info-value">{technician?.kullaniciAdi || '—'}</span>
              </div>
              <div className="info-item">
                <FieldLabel className="info-label" as="span">Telefon</FieldLabel>
                <span className="info-value">{technician?.telefon || '—'}</span>
              </div>
              <div className="info-item">
                <FieldLabel className="info-label" as="span">Sipariş Yetkisi</FieldLabel>
                <span className="info-value">
                  <OrderCreationPermissionBadge value={technician?.orderCreationPermission} />
                </span>
              </div>
              <div className="info-item">
                <FieldLabel className="info-label" as="span">Durum</FieldLabel>
                <span className="info-value">{technician?.aktif === false ? 'Pasif' : 'Aktif'}</span>
              </div>
            </div>
          </div>

          <div className="toolbar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
            <div className="status-tabs" style={{ margin: 0 }}>
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`status-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                  <span className="tab-count" style={{ marginLeft: '8px', opacity: 0.7 }}>
                    ({tab.id === 'future' ? categorized.future.length : tab.id === 'open' ? categorized.open.length : categorized.completed.length})
                  </span>
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`secondary-btn ${dateFilterLabel ? 'filter-active' : ''}`}
              onClick={() => setIsDateModalOpen(true)}
            >
              <i className="fas fa-filter" />
              <span>{dateFilterLabel || 'Tarih Filtrele'}</span>
            </button>
          </div>

          <TpAppointmentListTable
            appointments={tabAppointments}
            emptyMessage="Bu sekmede randevu bulunamadı"
          />
        </>
      )}

      <DateRangeFilterModal
        isOpen={isDateModalOpen}
        onClose={() => setIsDateModalOpen(false)}
        initialRange={dateRange}
        onApply={(range) => {
          setDateRange(range);
          setIsDateModalOpen(false);
        }}
      />
    </main>
  );
};

export default TechServiceTechnicianDetailPage;
